import { enterpriseClient } from '@/api';
import { useLocalStorage } from '@/hooks/usehooks-ts';
import { removeDebugHeader, setDebugHeader } from '@/utils/_shared/apiUtils';
import { AlertProps, SnackbarProps } from '@mui/material';
import { PropsWithChildren, createContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type NotificationParams = SnackbarProps & {
  alertProps?: AlertProps;
};

export type DebugContextType = {
  isDebugEnabled: boolean;
  debugOptions: { permissions: boolean };
  setDebugOptions: ({ permissions }: { permissions: boolean }) => void;
};

export const DebugContext = createContext<DebugContextType>({
  isDebugEnabled: false,
  debugOptions: { permissions: false },
  setDebugOptions: () => {},
});

export function DebugProvider({ children }: PropsWithChildren) {
  const [searchParams] = useSearchParams();
  const initialValueMemo = useMemo(() => {
    return {
      permissions: false,
    };
  }, []);
  const [debugOptions, setDebugOptions] = useLocalStorage(
    'debugOptions',
    initialValueMemo,
  );
  const debug = searchParams.get('debug');
  const [isDebugEnabled, setIsDebugEnabled] = useLocalStorage(
    `debug`,
    debug === 'true' && import.meta.env.VITE_DEPLOYMENT_TYPE !== 'production',
  );

  useEffect(() => {
    if (debug) {
      if (
        debug === 'true' &&
        import.meta.env.VITE_DEPLOYMENT_TYPE !== 'production'
      ) {
        setIsDebugEnabled(true);
      } else {
        setIsDebugEnabled(false);
      }
    }
  }, [debug, setIsDebugEnabled]);

  useEffect(() => {
    if (isDebugEnabled) {
      enterpriseClient.setDebugHeader();
      setDebugHeader();
    } else {
      enterpriseClient.removeDebugHeader();
      removeDebugHeader();
    }
  }, [isDebugEnabled]);

  return (
    <DebugContext.Provider
      value={{ isDebugEnabled, setDebugOptions, debugOptions }}
    >
      {children}
    </DebugContext.Provider>
  );
}
